import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, Text, Pressable, StyleSheet, ScrollView, TextInput,
  Platform,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { Template } from '../../../../../constants/Types';
import Style from '../../../../../constants/Style';
import Colors from '../../../../../constants/Colors';
import SpaceDropdown from '../../../../Tickets/components/TicketsFilter/components/SpaceDropdown';
import AreaDropdown from '../../../../Tickets/components/TicketsFilter/components/AreaDropdown';
import FloorDropdown from '../../../../Tickets/components/TicketsFilter/components/FloorDropdown';
import PressableStyled, { PressableState } from '../../../../../components/PressableStyled';
import DeleteIcon16 from '../../../../../components/16pxIcons/DeleteIcon16';
import { Area, Floor, Space } from '../../../../../components/Map/SetFloorsModal/FloorItem';
import isMobile from '../../../../../components/IsMobile';

// eslint-disable-next-line no-shadow, no-unused-vars
export enum DropdownSelection { CATEGORY, IS_NOT, VALUE }

// eslint-disable-next-line no-shadow, no-unused-vars
export enum AssetFilterCategory {
  // eslint-disable-next-line no-unused-vars
  NAME = 'Name', ID = 'ID', TEMPLATE = 'Template', LOCATION = 'Location', MODEL = 'Model',
  // eslint-disable-next-line no-unused-vars
  MANUFACTURER = 'Manufacturer', SERIAL_NUMBER = 'Serial number', DESCRIPTION = 'Description',
}

export type AssetFilterSelection = {
  category: AssetFilterCategory | null,
  is?: boolean,
  value?: string,
  displayValue?: string,
}

interface AssetFilterItemProps extends AssetFilterSelection {
  index: number,
  visibleFilterIndex: number,
  dropdownSelection: DropdownSelection,
  setDropdownSelection: Dispatch<SetStateAction<DropdownSelection>>,
  suggestions: string[] | Template[],
  selectedFilters,
  setSelectedFilters,
  closeDropdowns: () => void,
  selectedLocationId: string | null,
  templates: Template[],
  setVisibleFilterIndex: Dispatch<SetStateAction<number>>,
  floorDropdownShown: boolean,
  setFloorDropdownShown: Dispatch<SetStateAction<boolean>>,
  areaDropdownShown: boolean,
  setAreaDropdownShown: Dispatch<SetStateAction<boolean>>,
  spaceDropdownShown: boolean,
  setSpaceDropdownShown: Dispatch<SetStateAction<boolean>>,
  selectedFloor: Floor,
  setSelectedFloor: Dispatch<SetStateAction<Floor | null>>,
  selectedArea: Area,
  setSelectedArea: Dispatch<SetStateAction<Area | null>>,
  selectedSpace: Space,
  setSelectedSpace: Dispatch<SetStateAction<Space | null>>,
  closeSortDropdowns: () => void,
}

const AssetFilterItem = ({
  index, category, is, value, visibleFilterIndex, dropdownSelection, suggestions, selectedFilters,
  setSelectedFilters, closeDropdowns, templates, setVisibleFilterIndex, selectedLocationId,
  setDropdownSelection, displayValue, floorDropdownShown, setFloorDropdownShown, areaDropdownShown,
  setAreaDropdownShown, spaceDropdownShown, setSpaceDropdownShown, selectedFloor, setSelectedFloor,
  selectedArea, setSelectedArea, selectedSpace, setSelectedSpace, closeSortDropdowns,
}: AssetFilterItemProps) => {
  const getFilterCategoryTestId = (filterCategory: AssetFilterCategory) => `asset-filter-category-${filterCategory.toLowerCase().replace(/\s+/g, '-')}`;
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const filterToggled: boolean = visibleFilterIndex === index;
  const categoryDropdownToggled: boolean = filterToggled
    && dropdownSelection === DropdownSelection.CATEGORY;
  const isNotDropdownToggled: boolean = filterToggled
    && dropdownSelection === DropdownSelection.IS_NOT;
  const menusShown: boolean = !!category;
  const [searchString, setSearchString] = useState<string>(displayValue ?? value ?? '');
  const suggestionsDropdownToggled: boolean = suggestions?.length > 0
    && filterToggled && dropdownSelection === DropdownSelection.VALUE;
  const isLocation: boolean = category === AssetFilterCategory.LOCATION;
  const remainingFilters: AssetFilterCategory[] = Object.values(AssetFilterCategory)
    .filter((item: AssetFilterCategory) => !(selectedFilters
      .map((filter: AssetFilterSelection) => filter.category?.trim().toLowerCase()))
      .includes(item?.trim().toLowerCase()));
  const noRemainingFilters: boolean = remainingFilters.length === 0;

  const setFilterValue = (newValue: string, newDisplayValue?: string) => {
    const updatedFilters = [...selectedFilters
      .map((filter: AssetFilterSelection) => ({ ...filter }))];
    updatedFilters[index].value = newValue;
    if (newDisplayValue) updatedFilters[index].displayValue = newDisplayValue;
    setSelectedFilters(updatedFilters);
    closeDropdowns();
  };

  const setFilterCategory = (newCategory: AssetFilterCategory) => {
    setSearchString('');
    const updatedFilters = [...selectedFilters
      .map((filter: AssetFilterSelection) => ({ ...filter }))];
    if (!updatedFilters[index].category) updatedFilters[index].is = true;
    updatedFilters[index].category = newCategory;
    updatedFilters[index].value = null;
    setSelectedFilters(updatedFilters);
    closeDropdowns();
  };

  const setIsOrNot = (newValue: boolean) => {
    const updatedFilters = [...selectedFilters];
    updatedFilters[index].is = newValue;
    setSelectedFilters(updatedFilters);
    closeDropdowns();
  };

  const removeFilter: () => void = () => {
    if (selectedFilters[index].category === AssetFilterCategory.LOCATION) {
      setSelectedFloor(null);
      setSelectedArea(null);
      setSelectedSpace(null);
    }
    const updatedFilters = [...selectedFilters];
    updatedFilters.splice(index, 1);
    setSelectedFilters(updatedFilters);
  };

  return <View
    style={[
      {
        flexDirection: 'row',
        zIndex: -1 * index,
        paddingVertical: 6,
      },
    ]}
    key={`filter_${index}`}
  >
    <View style={styles.filterRow}>
      <View style={[styles.menu, { zIndex: 2 }]}>
        <Pressable
          style={styles.dropdownButton}
          onPress={() => {
            setDropdownSelection(DropdownSelection.CATEGORY);
            setVisibleFilterIndex(categoryDropdownToggled ? null : index);
            setFloorDropdownShown(false);
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
            closeSortDropdowns();
          }}
          disabled={noRemainingFilters}
          testID='select-filter-btn'
        >
          <Text style={Style.dropdownText}>{category ?? 'Select filter'}</Text>
          <FontAwesome
            name={categoryDropdownToggled ? 'angle-up' : 'angle-down'}
            size={24}
            color={noRemainingFilters
              ? Colors.light.hereworksBlue50 : Colors.light.hereworksBlue500}
          />
        </Pressable>
        <View>
          {categoryDropdownToggled && <ScrollView
            style={styles.dropdownContent}
            nestedScrollEnabled
          >
            {remainingFilters.map((item: AssetFilterCategory) => <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.menuItem,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              onPress={() => setFilterCategory(item)}
              key={`category_${item}`}
              testID={getFilterCategoryTestId(item)}
            >
              <Text style={Style.dropdownText}>{item}</Text>
            </Pressable>)}
          </ScrollView>}
        </View>
      </View>
      {false && menusShown && <View style={styles.menu}>
        <Pressable
          style={[styles.dropdownButton, styles.isNotDropdown]}
          onPress={() => {
            setDropdownSelection(DropdownSelection.IS_NOT);
            setVisibleFilterIndex(isNotDropdownToggled ? null : index);
            closeSortDropdowns();
          }}
        >
          <Text style={Style.dropdownText}>{is ? 'Is' : 'Not'}</Text>
          <FontAwesome
            name={isNotDropdownToggled ? 'angle-up' : 'angle-down'}
            size={24}
            color={Colors.light.hereworksBlue500}
          />
        </Pressable>
        <View>
          {isNotDropdownToggled
            && <View style={[styles.dropdownContent, styles.isNotDropdown]}>
              {['Is', 'Not'].map((name: string, itemIndex: number) => <Pressable
                style={({ hovered, pressed }) => [
                  styles.menuItem,
                  hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                ]}
                onPress={() => setIsOrNot(!itemIndex)}
                key={`is/not_${itemIndex}`}
              >
                <Text style={Style.dropdownText}>{name}</Text>
              </Pressable>)}
            </View>}
        </View>
      </View>}
      {!isMobile && menusShown && <Text style={styles.isText}>{'is'}</Text>}
      {menusShown && <View style={[styles.menu, isLocation && { flex: 1 }]}>
        {isLocation ? <View style={styles.locationDropdownSection}>
          {Object.values(selectedArea?.spaces ?? {}).length > 0 && <SpaceDropdown
            {...{
              selectedSpace,
              setFloorDropdownShown,
              setAreaDropdownShown,
              setSpaceDropdownShown,
              setSelectedSpace,
              spaceDropdownShown,
            }}
            area={selectedArea}
            closeOtherDropdowns={() => {
              setVisibleFilterIndex(null);
              setFloorDropdownShown(false);
              setAreaDropdownShown(false);
              closeSortDropdowns();
            }}
            hideLabel
            listPositionAbsolute
            style={{ width: styles.dropdownButton.width }}
          />}
          {Object.values(selectedFloor?.areas ?? {})?.length > 0 && <AreaDropdown
            {...{
              selectedArea,
              setFloorDropdownShown,
              setAreaDropdownShown,
              setSpaceDropdownShown,
              setSelectedArea,
              setSelectedSpace,
              areaDropdownShown,
            }}
            floor={selectedFloor}
            closeOtherDropdowns={() => {
              setVisibleFilterIndex(null);
              setFloorDropdownShown(false);
              setSpaceDropdownShown(false);
              closeSortDropdowns();
            }}
            hideLabel
            listPositionAbsolute
            style={{ width: styles.dropdownButton.width }}
          />}
          <FloorDropdown
            {...{
              selectedFloor,
              selectedLocationId,
              setFloorDropdownShown,
              setSelectedFloor,
              setSelectedArea,
              setSelectedSpace,
              floorDropdownShown,
            }}
            locations={buildings
              ? Object.values(buildings[selectedBuildingId]?.floors ?? {}) ?? [] : []}
            closeOtherDropdowns={() => {
              setVisibleFilterIndex(null);
              setAreaDropdownShown(false);
              setSpaceDropdownShown(false);
              closeSortDropdowns();
            }}
            hideLabel
            listPositionAbsolute
            style={{ width: styles.dropdownButton.width }}
          />
        </View> : <Pressable>
          <TextInput
            onStartShouldSetResponder={() => true}
            onTouchEnd={(e) => e.stopPropagation()}
            placeholder={suggestions?.length ? 'View suggestions'
              : 'Enter search value'}
            placeholderTextColor={Colors.light.hereworksBlack400}
            style={styles.dropdownButton}
            onFocus={() => {
              setDropdownSelection(DropdownSelection.VALUE);
              setVisibleFilterIndex(index);
              closeSortDropdowns();
            }}
            value={searchString}
            onChangeText={(text: string) => {
              setSearchString(text);
              if (!suggestions?.length) setFilterValue(text);
            }}
          />
        </Pressable>}
        <View>
          {suggestionsDropdownToggled && <ScrollView
            style={styles.dropdownContent}
            nestedScrollEnabled
            testID='filters-template-list'
          >
            {category === AssetFilterCategory.TEMPLATE ? templates
              .filter((template: Template) => template?.name?.toLowerCase()
                .includes(searchString?.trim().toLowerCase()))
              .map((template: Template) => <Pressable
                onPress={() => {
                  setSearchString(template.name);
                  setFilterValue(template.id, template.name);
                  closeSortDropdowns();
                }}
                style={({ hovered, pressed }: PressableState) => [
                  styles.menuItem,
                  hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                ]}
                key={`value_${template.id}_${index}`}
              >
                <Text style={Style.dropdownText}>
                  {template.name}
                </Text>
              </Pressable>)
              : suggestions.filter((option: string) => option?.trim().toLowerCase()
                .includes(searchString?.trim().toLowerCase()))
                .map((option: string) => <Pressable
                  onPress={() => {
                    setSearchString(option);
                    setFilterValue(option);
                    closeSortDropdowns();
                  }}
                  style={({ hovered, pressed }: PressableState) => [
                    styles.menuItem,
                    hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                  ]}
                  key={`value_${option}_${index}`}
                >
                  <Text style={Style.dropdownText}>
                    {option}
                  </Text>
                </Pressable>)}
          </ScrollView>}
        </View>
      </View>}
    </View>
    <PressableStyled
      onPress={removeFilter}
      style={styles.deleteIcon}
      testID='remove-filter-btn'
    >
      <DeleteIcon16
        color={isMobile ? Colors.light.hereworksRed500 : Colors.light.hereworksDarkBlue300}
      />
    </PressableStyled>
  </View>;
};

const styles = StyleSheet.create({
  filterRow: {
    flexDirection: isMobile ? 'column' : 'row',
    flex: 1,
  },
  menu: {
    marginEnd: 8,
    alignSelf: 'stretch',
  },
  menuItem: {
    paddingHorizontal: 12,
    paddingVertical: 9,
    alignSelf: 'stretch',
    width: isMobile ? undefined : 200,
  },
  deleteIcon: {
    width: isMobile ? 16 : 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dropdownContent: {
    ...Style.dropdown,
    position: Platform.OS === 'android' ? undefined : 'absolute',
    flexDirection: 'column',
    alignItems: undefined,
    height: undefined,
    top: 4,
    maxHeight: isMobile ? 117 : 200,
  },
  dropdownButton: {
    ...Style.dropdown,
    ...Style.row,
    paddingHorizontal: 12,
    paddingVertical: 9,
    width: 200,
    marginTop: isMobile ? 6 : undefined,
  },
  isNotDropdown: {
    width: 100,
  },
  isText: {
    ...Style.dropdownText,
    marginTop: 10,
    marginStart: 8,
    marginEnd: 16,
  },
  locationDropdownSection: {
    flexDirection: 'column-reverse',
    marginTop: isMobile ? 8 : undefined,
    zIndex: 0,
  },
});

export default AssetFilterItem;
